/**
 * Règles de paiement à la réservation.
 * - Domicile : acompte de 30 € en ligne, solde le jour du rendez-vous (+ supplément éventuel)
 * - Visio : paiement de la totalité en ligne
 * - Cabinet : aucun paiement en ligne, règlement sur place
 */

export const DEPOSIT_AMOUNT = 30;

export const PAID_BOOKING_SERVICES = [
  "Bilan naturopathique – Enfants & Adolescents",
  "Bilan naturopathique – Adultes",
  "PARCOURS FAMILLE COURAGEUSE",
];

/** Accompagnements payables en ligne uniquement lors d'une réservation en visio */
export const VISIO_ONLY_PAID_SERVICES = [
  "Suivi naturopathique – Adultes",
  "Suivi naturopathique – Enfants & adolescents",
];

const normalize = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export function isPaidBookingService(title?: string | null): boolean {
  if (!title) return false;
  const n = normalize(title);
  return PAID_BOOKING_SERVICES.some((s) => normalize(s) === n);
}

export function isVisioOnlyPaidService(title?: string | null): boolean {
  if (!title) return false;
  const n = normalize(title);
  return VISIO_ONLY_PAID_SERVICES.some((s) => normalize(s) === n);
}


export function parsePrice(tarif?: string | number | null): number {
  if (typeof tarif === "number") return tarif;
  if (!tarif) return 0;
  const match = String(tarif).replace(",", ".").match(/[\d]+(\.[\d]+)?/);
  return match ? parseFloat(match[0]) : 0;
}

export interface BookingPayment {
  /** Le paiement en ligne est requis pour valider la réservation */
  required: boolean;
  /** Montant à régler en ligne */
  amountToPay: number;
  /** Prix total de l'accompagnement (hors supplément) */
  price: number;
  /** Supplément de déplacement éventuel */
  surcharge: number;
  /** Solde restant à régler le jour du rendez-vous */
  balance: number;
  /** Réduction appliquée via un code promo */
  discount: number;
  mode: "none" | "deposit" | "full";
}

export function getBookingPayment(params: {
  serviceTitle?: string | null;
  tarif?: string | number | null;
  isOnline?: boolean;
  isHomeVisit?: boolean;
  surcharge?: number;
  /** Réduction éventuelle (code promo validé) */
  discount?: number;
}): BookingPayment {
  const price = parsePrice(params.tarif);
  const surcharge = params.surcharge || 0;
  const visioOnlyPaid = isVisioOnlyPaidService(params.serviceTitle) && !!params.isOnline && !params.isHomeVisit;
  const eligible = (isPaidBookingService(params.serviceTitle) || visioOnlyPaid) && price > 0;

  if (!eligible) {
    return { required: false, amountToPay: 0, price, surcharge, balance: price + surcharge, discount: 0, mode: "none" };
  }

  const applyDiscount = (base: number) => {
    const discount = Math.min(Math.max(params.discount || 0, 0), base);
    return { discount, amountToPay: Math.max(0, base - discount) };
  };

  if (params.isHomeVisit) {
    const deposit = Math.min(DEPOSIT_AMOUNT, price);
    const { discount, amountToPay } = applyDiscount(deposit);
    return {
      required: amountToPay > 0,
      amountToPay,
      price,
      surcharge,
      balance: Math.max(0, price - deposit) + surcharge,
      discount,
      mode: "deposit",
    };
  }

  if (params.isOnline) {
    const { discount, amountToPay } = applyDiscount(price);
    return { required: amountToPay > 0, amountToPay, price, surcharge: 0, balance: 0, discount, mode: "full" };
  }

  // Cabinet : règlement sur place
  return { required: false, amountToPay: 0, price, surcharge: 0, balance: price, discount: 0, mode: "none" };
}

export const formatEuro = (value: number) =>
  `${Number.isInteger(value) ? value : value.toFixed(2)} €`;
